import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { mutate as mutateGlobal } from 'swr'
import { Alert, Button, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faCoins, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import useSWRFetch from '../../helpers/hooks/useSWRFetch'
import { getRedeemErrorMessage, getRedemptionLimits, redeemCoins } from '../../helpers/api/rewards'
import { Toast } from '../UI/toast/Toast'
import { formatNumber, formatUsd } from './rewardsUtils'

// HO-TASK-285: canje de Homelan Coins dentro del flujo de pago del propietario.
//
// El canje NO paga una deuda directamente: el backend convierte los HC en un
// abono aprobado que suma al saldo a favor de la propiedad (RedemptionService
// en HO-TASK-272). Desde ahí se paga con el flujo que ya existe, eligiendo
// "Caja" como origen de fondos. Por eso este panel no toca la lógica del pago:
// canjea, avisa con onRedeemed para que la pantalla refresque el saldo, y
// opcionalmente ofrece pasar a "Caja" con onUseBalance.

const LIMITS_KEY = 'rewards-redemption-limits'

const newIdempotencyKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? `canje-${crypto.randomUUID()}`
    : `canje-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

// Mismo truncado que el backend (RewardCalculator::truncateMoney), para que el
// monto que se promete acá sea exactamente el que se abona.
const hcToUsd = (hc, hcPerUsd) => (hcPerUsd > 0 ? Math.floor((hc / hcPerUsd) * 1e6) / 1e6 : 0)

const RedeemCoinsPanel = ({ debtUsd, balanceUsd, onRedeemed, onUseBalance }) => {
  const { data, error, isLoading, mutate } = useSWRFetch(LIMITS_KEY, getRedemptionLimits)
  const limits = data?.data

  const [hcInput, setHcInput] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [redeemError, setRedeemError] = useState(null)
  const [lastRedemption, setLastRedemption] = useState(null)
  // Clave del intento en curso: se crea al abrir la confirmación y se reusa si
  // el mismo intento se reenvía, así un reintento no gasta las monedas dos veces.
  const [idempotencyKey, setIdempotencyKey] = useState(null)
  // Candado síncrono contra el doble clic: el estado `submitting` recién se
  // actualiza en el siguiente render, así que dos clics seguidos lo verían en
  // false. El backend igual es idempotente por la clave, esto evita el viaje.
  const inFlight = useRef(false)

  const debt = Math.max(Number(debtUsd) || 0, 0)
  const hcBalance = Math.floor(Number(limits?.hc_balance) || 0)
  const hcPerUsd = Number(limits?.hc_per_usd) || 0
  const minHc = Number(limits?.min_hc) || 0
  const maxHcRule = limits?.max_hc === null || limits?.max_hc === undefined ? Infinity : Number(limits.max_hc)

  // Las Coins solo tienen que cubrir lo que el saldo a favor no alcanza: sin
  // descontarlo, después de un canje (que suma a ese saldo) el panel volvía a
  // proponer canjear la deuda entera y se terminaba cubriendo dos veces.
  const saldo = Math.max(Number(balanceUsd) || 0, 0)
  const debtToCover = Math.max(debt - saldo, 0)

  // HC que cubren lo que falta. Si vale menos que el mínimo de canje, se
  // permite canjear el mínimo: el excedente queda como saldo a favor.
  const hcToCoverDebt = Math.ceil(debtToCover * hcPerUsd)
  const maxHc = Math.min(hcBalance, maxHcRule, Math.max(hcToCoverDebt, minHc))

  const hc = Math.floor(Number(hcInput) || 0)
  const usd = hcToUsd(hc, hcPerUsd)
  const remaining = Math.max(debtToCover - usd, 0)
  const surplus = Math.max(usd - debtToCover, 0)
  const coverableUsd = Math.min(hcToUsd(hcBalance, hcPerUsd), debtToCover)

  const validation = useMemo(() => {
    if (!hcInput) return null
    if (hc < minHc) return `El canje mínimo es de ${formatNumber(minHc)} HC.`
    if (hc > hcBalance) return `Solo tienes ${formatNumber(hcBalance)} HC disponibles.`
    if (hc > maxHcRule) return `El canje máximo es de ${formatNumber(maxHcRule)} HC por operación.`
    if (hc > maxHc) return `Con ${formatNumber(maxHc)} HC ya cubres toda la deuda seleccionada.`
    return null
  }, [hcInput, hc, minHc, hcBalance, maxHcRule, maxHc])

  // El panel es un complemento del pago: si Rewards no responde, el flujo de
  // pago sigue igual y el panel simplemente no aparece.
  if (error || (!isLoading && !limits)) return null

  if (isLoading) {
    return (
      <div className="border border-200 rounded-soft p-3 mb-3 d-flex align-items-center fs--1 text-600">
        <Spinner size="sm" color="warning" className="mr-2" /> Consultando tus Homelan Coins...
      </div>
    )
  }

  const openConfirmation = () => {
    setRedeemError(null)
    setIdempotencyKey(newIdempotencyKey())
    setConfirming(true)
  }

  const closeConfirmation = () => {
    if (submitting) return
    setConfirming(false)
    setIdempotencyKey(null)
  }

  const handleRedeem = async () => {
    if (inFlight.current) return
    inFlight.current = true
    setSubmitting(true)
    setRedeemError(null)

    try {
      const { data: result } = await redeemCoins({ hcAmount: hc, idempotencyKey })
      setLastRedemption(result)
      setConfirming(false)
      setIdempotencyKey(null)
      setHcInput('')
      Toast.fire({ type: 'success', title: `Canjeaste ${formatNumber(result.hc_amount)} HC por ${formatUsd(result.usd_amount)}.` })
      onRedeemed && onRedeemed(result)
      // El resumen de Rewards (vista y widget del dashboard) también cambió.
      mutateGlobal('rewards-summary')
    } catch (err) {
      setRedeemError(getRedeemErrorMessage(err))
    } finally {
      inFlight.current = false
      setSubmitting(false)
      mutate()
    }
  }

  const preventSubmitOnEnter = (event) => {
    // El panel vive dentro del formulario de pago: Enter no debe reportar el pago.
    if (event.key === 'Enter') event.preventDefault()
  }

  const balanceCoversDebt = debt > 0 && debtToCover === 0

  return (
    <div className="border border-200 rounded-soft p-3 mb-3">
      <div className="d-flex align-items-center flex-wrap mb-2">
        <div
          className="d-flex flex-center rounded-circle bg-soft-warning text-warning mr-2 flex-shrink-0"
          style={{ width: '2.25rem', height: '2.25rem' }}
        >
          <FontAwesomeIcon icon={faCoins} />
        </div>
        <div className="flex-grow-1">
          <h6 className="mb-0">Paga con tus Homelan Coins</h6>
          <span className="fs--1 text-600">
            Tienes <b className="text-900">{formatNumber(hcBalance)} HC</b>
            {hcPerUsd > 0 && <> (equivalen a {formatUsd(hcToUsd(hcBalance, hcPerUsd))})</>}
          </span>
        </div>
      </div>

      {lastRedemption && (
        <Alert color="success" className="fs--1 mb-3">
          <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
          Canjeaste <b>{formatNumber(lastRedemption.hc_amount)} HC</b> y se abonaron{' '}
          <b>{formatUsd(lastRedemption.usd_amount)}</b> a tu saldo a favor. Te quedan{' '}
          {formatNumber(lastRedemption.hc_balance)} HC.
          {!balanceCoversDebt && (
            <div className="mt-2">
              Para usar tu saldo a favor, elige "Caja" como origen de fondos. Si no alcanza para todo, paga el resto por
              transferencia o en efectivo.
            </div>
          )}
        </Alert>
      )}

      {!limits.can_redeem ? (
        <p className="fs--1 text-600 mb-0">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          Puedes canjear a partir de <b className="text-900">{formatNumber(minHc)} HC</b>
          {hcBalance < minHc && <> (te faltan {formatNumber(minHc - hcBalance)} HC)</>}. Sigue pagando a tiempo para
          acumular más.
        </p>
      ) : debt <= 0 ? (
        <p className="fs--1 text-600 mb-0">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          Selecciona el período y las cuentas a pagar para ver cuánto puedes cubrir con tus Coins.
        </p>
      ) : balanceCoversDebt ? (
        <div className="fs--1 text-700">
          <FontAwesomeIcon icon={faCheckCircle} className="mr-1 text-success" />
          Tu saldo a favor de <b>{formatUsd(saldo)}</b> ya cubre los {formatUsd(debt)} a pagar: no necesitas canjear
          Coins. Elige "Caja" como origen de fondos para pagar con él.
          {onUseBalance && (
            <div className="mt-2">
              <Button type="button" color="success" size="sm" onClick={onUseBalance}>
                Pagar con mi saldo a favor
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
          <p className="fs--1 text-700 mb-2">
            Tus Coins cubren hasta <b>{formatUsd(coverableUsd)}</b> de los {formatUsd(debtToCover)}
            {saldo > 0 ? ' que tu saldo a favor no alcanza a cubrir' : ' a pagar'}.
          </p>

          <Row className="align-items-center">
            <Col xs={12} md={5} className="mb-2">
              <div className="d-flex align-items-center">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={minHc}
                  max={maxHc}
                  step={1}
                  value={hcInput}
                  placeholder={`${formatNumber(minHc)} a ${formatNumber(maxHc)}`}
                  onKeyDown={preventSubmitOnEnter}
                  onChange={(event) => setHcInput(event.target.value)}
                  invalid={!!validation}
                  aria-label="Homelan Coins a canjear"
                />
                <span className="ml-2 fs--1 text-600">HC</span>
              </div>
            </Col>
            <Col xs={12} md={7} className="mb-2">
              <Button type="button" color="falcon-default" size="sm" className="mr-2 mb-1" onClick={() => setHcInput(String(minHc))}>
                Mínimo
              </Button>
              <Button type="button" color="falcon-default" size="sm" className="mb-1" onClick={() => setHcInput(String(maxHc))}>
                {maxHc >= hcToCoverDebt ? 'Cubrir toda la deuda' : 'Usar todas mis Coins'}
              </Button>
            </Col>
          </Row>

          {validation && <div className="fs--1 text-danger mb-2">{validation}</div>}

          {hc > 0 && !validation && (
            <div className="bg-light rounded-soft p-2 fs--1 mb-2">
              <div className="d-flex justify-content-between">
                <span>Total a pagar</span>
                <span>{formatUsd(debt)}</span>
              </div>
              {saldo > 0 && (
                <div className="d-flex justify-content-between">
                  <span>Tu saldo a favor</span>
                  <span className="text-success">− {formatUsd(Math.min(saldo, debt))}</span>
                </div>
              )}
              <div className="d-flex justify-content-between">
                <span>Canjeas {formatNumber(hc)} HC</span>
                <span className="text-success font-weight-semi-bold">− {formatUsd(Math.min(usd, debtToCover))}</span>
              </div>
              <div className="d-flex justify-content-between font-weight-semi-bold">
                <span>Te quedaría por pagar</span>
                <span className="text-900">{formatUsd(remaining)}</span>
              </div>
              {surplus > 0 && (
                <div className="text-600 mt-1">El excedente de {formatUsd(surplus)} queda como saldo a favor.</div>
              )}
            </div>
          )}

          <Button type="button" color="warning" size="sm" disabled={!hc || !!validation} onClick={openConfirmation}>
            <FontAwesomeIcon icon={faCoins} className="mr-1" />
            Canjear {hc > 0 && !validation ? `${formatNumber(hc)} HC` : 'Coins'}
          </Button>
        </>
      )}

      <Modal isOpen={confirming} toggle={closeConfirmation} centered>
        <ModalHeader toggle={closeConfirmation}>Confirma tu canje</ModalHeader>
        <ModalBody>
          <p className="mb-2">
            Vas a canjear <b>{formatNumber(hc)} Homelan Coins</b> por <b>{formatUsd(usd)}</b>.
          </p>
          <ul className="fs--1 text-700 pl-3 mb-2">
            <li>Las Coins se descuentan de tu saldo y te quedarán {formatNumber(Math.max(hcBalance - hc, 0))} HC.</li>
            <li>El monto se abona a tu cuenta del condominio como saldo a favor.</li>
            <li>Tu nivel y tu XP no cambian.</li>
          </ul>
          <p className="fs--1 font-weight-semi-bold text-danger mb-0">Este canje no se puede deshacer.</p>
          {redeemError && (
            <Alert color="danger" className="fs--1 mt-3 mb-0">
              {redeemError}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="secondary" onClick={closeConfirmation} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="button" color="warning" onClick={handleRedeem} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="mr-2" /> Canjeando...
              </>
            ) : (
              `Sí, canjear ${formatNumber(hc)} HC`
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

RedeemCoinsPanel.propTypes = {
  debtUsd: PropTypes.number,
  balanceUsd: PropTypes.number,
  onRedeemed: PropTypes.func,
  onUseBalance: PropTypes.func
}

export default RedeemCoinsPanel
